import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  api,
  ClientSummary,
  CommitmentStatus,
  ClientBrief,
  ClientBriefType,
  ClientStatusHistory,
  MeetingSentiment,
  SentimentCap8,
  ClientStatus,
  ClientAsksResponse,
  MoodGridResponse,
  Branch,
} from '../lib/api';
import { BranchChip } from '../components/BranchChip';

const STATUS_LABELS: Record<string, string> = {
  live_client: 'Live Client',
  signed_client: 'Signed Client',
  prospect_engaged: 'Prospect (Engaged)',
  prospect_early: 'Prospect (Early)',
  past: 'Past',
};

const STATUS_OPTIONS: Array<{ value: ClientStatus; label: string }> = [
  { value: 'prospect_early', label: 'Prospect (Early)' },
  { value: 'prospect_engaged', label: 'Prospect (Engaged)' },
  { value: 'signed_client', label: 'Signed Client' },
  { value: 'live_client', label: 'Live Client' },
  { value: 'past', label: 'Past' },
];

const OWNER_LABELS: Record<string, string> = {
  cap8: 'Cap8',
  client: 'Client',
  shared: 'Shared',
  unclear: 'Unclear',
};

const CADENCE_LABELS: Record<string, string> = {
  one_time: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  custom: 'Custom',
};

const CAP8_COLORS: Record<SentimentCap8, string> = {
  negative: '#ef4444',
  neutral: '#6b7280',
  positive: '#86efac',
  very_positive: '#22c55e',
};

const CAP8_LABELS: Record<SentimentCap8, string> = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
  very_positive: 'Very Positive',
};

const STRATEGY_LABELS: Record<string, string> = {
  low_volatility: 'Low Volatility',
  sp500_tilts: 'S&P 500 Tilts',
  sector_rotation: 'Sector Rotation',
  dynamic_hedge: 'Dynamic Hedge',
  risk_overlay: 'Risk Overlay',
  core_equity: 'Core Equity',
  novel: 'Novel',
};

const POSITION_LABELS: Record<string, string> = {
  using_it: 'Using',
  considering_it: 'Considering',
  asking_about_it: 'Asking',
  pushed_back_on_it: 'Pushed Back',
  mentioned_in_passing: 'In Passing',
  recommending_against_it: 'Against',
};

type TabType = 'overview' | 'commitments' | 'sentiment' | 'asks';

// ── helpers ────────────────────────────────────────────────────────────────

const TODAY = new Date().toISOString().slice(0, 10);
const UPCOMING_DAYS = 14;

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  return dueDate < TODAY;
}

function isUpcoming(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() + UPCOMING_DAYS);
  return dueDate >= TODAY && dueDate <= cutoff.toISOString().slice(0, 10);
}

function isStaleWaiting(meetingDate: string): boolean {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - UPCOMING_DAYS);
  return meetingDate <= cutoff.toISOString().slice(0, 10);
}

// Simple markdown renderer (ported from original)
function renderMarkdown(text: string): string {
  return text
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '');
}

// ── component ──────────────────────────────────────────────────────────────

export function ClientDetail() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive active tab from ?tab= (default overview)
  const rawTab = searchParams.get('tab') as TabType | null;
  const activeTab: TabType = rawTab && ['overview', 'commitments', 'sentiment', 'asks'].includes(rawTab)
    ? (rawTab as TabType)
    : 'overview';

  // ── shared state ──
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // ── overview state ──
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [statusHistory, setStatusHistory] = useState<ClientStatusHistory[]>([]);
  const [showStatusHistory, setShowStatusHistory] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [moodData, setMoodData] = useState<MeetingSentiment[]>([]);
  const [hoveredMeeting, setHoveredMeeting] = useState<MeetingSentiment | null>(null);
  const [briefType, setBriefType] = useState<ClientBriefType>('internal');
  const [internalBrief, setInternalBrief] = useState<ClientBrief | null>(null);
  const [shareableBrief, setShareableBrief] = useState<ClientBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [shareableConfirmOpen, setShareableConfirmOpen] = useState(false);

  // ── commitments tab state ──
  const [commitmentSummary, setCommitmentSummary] = useState<ClientSummary | null>(null);
  const [commitmentsLoaded, setCommitmentsLoaded] = useState(false);
  const [commitmentsLoading, setCommitmentsLoading] = useState(false);

  // ── sentiment tab state ──
  const [moodGrid, setMoodGrid] = useState<MoodGridResponse | null>(null);
  const [sentimentLoaded, setSentimentLoaded] = useState(false);
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [expandedMeetings, setExpandedMeetings] = useState<Set<string>>(new Set());

  // ── asks tab state ──
  const [asks, setAsks] = useState<ClientAsksResponse | null>(null);
  const [asksLoaded, setAsksLoaded] = useState(false);
  const [asksLoading, setAsksLoading] = useState(false);

  // Track which tabs have been activated (lazy load)
  const activatedTabs = useRef<Set<TabType>>(new Set());

  // ── initial load ──
  useEffect(() => {
    if (!clientId) return;
    Promise.all([
      api.getClientSummary(clientId),
      api.getMoodGrid({ client_ids: [clientId], limit: 8 }),
      api.getClientBrief(clientId, 'internal').catch(() => null),
    ]).then(([sum, mood, brief]) => {
      setSummary(sum);
      if (mood.clients.length > 0) setMoodData(mood.clients[0].meetings);
      setInternalBrief(brief);
    }).catch(() => {
      setError('Failed to load client');
    }).finally(() => {
      setLoading(false);
    });
  }, [clientId]);

  // ── tab change handler (lazy load) ──
  const handleTabChange = (tab: TabType) => {
    setSearchParams({ tab }, { replace: true });
    if (activatedTabs.current.has(tab)) return;
    activatedTabs.current.add(tab);

    if (tab === 'commitments' && !commitmentsLoaded) {
      setCommitmentsLoading(true);
      api.getClientSummary(clientId!).then(data => {
        setCommitmentSummary(data);
        setCommitmentsLoaded(true);
      }).catch(() => setError('Failed to load commitments')).finally(() => setCommitmentsLoading(false));
    }

    if (tab === 'sentiment' && !sentimentLoaded) {
      setSentimentLoading(true);
      api.getMoodGrid({ client_ids: [clientId!], limit: 20 }).then(data => {
        setMoodGrid(data);
        setSentimentLoaded(true);
      }).catch(() => setError('Failed to load sentiment')).finally(() => setSentimentLoading(false));
    }

    if (tab === 'asks' && !asksLoaded) {
      setAsksLoading(true);
      api.getClientAsks(clientId!).then(data => {
        setAsks(data);
        setAsksLoaded(true);
      }).catch(() => setError('Failed to load asks')).finally(() => setAsksLoading(false));
    }
  };

  // ── overview helpers ──
  const loadBrief = async (type: ClientBriefType, forceRefresh = false) => {
    if (!clientId) return;
    setBriefLoading(true);
    try {
      const brief = await api.getClientBrief(clientId, type, forceRefresh);
      if (type === 'internal') setInternalBrief(brief);
      else setShareableBrief(brief);
    } catch {
      setError(`Failed to load ${type} brief`);
    } finally {
      setBriefLoading(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  const downloadMarkdown = (brief: ClientBrief, filename: string) => {
    const blob = new Blob([brief.brief_text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleStatusChange = async (newStatus: ClientStatus) => {
    if (!clientId || !summary) return;
    setStatusChanging(true);
    try {
      await api.updateClient(clientId, { status: newStatus });
      const updated = await api.getClientSummary(clientId);
      setSummary(updated);
      setShowStatusDropdown(false);
      if (showStatusHistory) {
        const hist = await api.getClientStatusHistory(clientId);
        setStatusHistory(hist);
      }
    } catch {
      setError('Failed to update status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleShowStatusHistory = async () => {
    if (!showStatusHistory && statusHistory.length === 0) {
      try {
        const hist = await api.getClientStatusHistory(clientId!);
        setStatusHistory(hist);
      } catch {
        // silent
      }
    }
    setShowStatusHistory(v => !v);
  };

  const getSentimentDot = (meeting: MeetingSentiment) => {
    const sentiment = meeting.sentiment_cap8;
    if (!sentiment) return { color: '#e5e7eb', opacity: 0.4 };
    return { color: CAP8_COLORS[sentiment], opacity: meeting.sentiment_cap8_confidence === 'low' ? 0.4 : 1 };
  };

  const toggleMeeting = (id: string) => {
    setExpandedMeetings(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── commitments helpers ──
  const handleCommitmentStatus = async (commitmentId: string, status: CommitmentStatus) => {
    try {
      await api.updateCommitmentStatus(commitmentId, status);
      if (commitmentSummary) {
        const updated = await api.getClientSummary(clientId!);
        setCommitmentSummary(updated);
      }
    } catch {
      setError('Failed to update commitment');
    }
  };

  const handleStandingItemComplete = async (id: string) => {
    try {
      await api.markStandingItemComplete(id);
      const updated = await api.getClientSummary(clientId!);
      setCommitmentSummary(updated);
    } catch {
      setError('Failed to complete standing item');
    }
  };

  // ── early returns ──
  if (loading) return <div className="loading">Loading...</div>;
  if (!summary) return <div className="alert alert-error">Client not found</div>;

  const { client } = summary;

  // ── render helpers ──
  const renderBriefContent = (brief: ClientBrief | null, type: ClientBriefType) => {
    if (briefLoading) return <div className="loading">Generating brief...</div>;
    if (!brief) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p className="text-muted">Brief not yet generated.</p>
          <button className="btn btn-primary" onClick={() => loadBrief(type)}>
            Generate {type === 'internal' ? 'Internal' : 'Client'} Brief
          </button>
        </div>
      );
    }
    return (
      <div>
        {type === 'shareable' && (
          <div style={{ background: 'var(--accent-coral)', color: 'white', padding: '0.5rem 1rem', borderRadius: '4px', marginBottom: '1rem', display: 'inline-block' }}>
            This brief is appropriate for client sharing
          </div>
        )}
        {brief.is_stale && (
          <div className="brief-stale-banner">
            Based on data from {new Date(brief.created_at).toLocaleDateString()}.
            {brief.new_insights_since > 0 && ` ${brief.new_insights_since} new insights since then.`}
            <button className="btn btn-secondary btn-small" style={{ marginLeft: '1rem' }} onClick={() => loadBrief(type, true)}>
              Regenerate
            </button>
          </div>
        )}
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            Generated: {new Date(brief.created_at).toLocaleString()}
          </span>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>|</span>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            {brief.insight_count} insights ({brief.date_range_start} to {brief.date_range_end})
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary btn-small" onClick={() => copyToClipboard(brief.brief_text)}>Copy</button>
            <button className="btn btn-secondary btn-small" onClick={() => downloadMarkdown(brief, `${client.name}_${type}_brief.md`)}>Download</button>
            <button className="btn btn-secondary btn-small" onClick={() => loadBrief(type, true)}>Regenerate</button>
          </div>
        </div>
        <div className="brief-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(brief.brief_text) }} />
      </div>
    );
  };

  // ── render ──
  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <button className="btn btn-secondary btn-small" onClick={() => navigate('/clients')} style={{ marginBottom: '1rem' }}>
          Back to Clients
        </button>
        <h1 className="page-title">{client.name}</h1>

        <p className="page-subtitle" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          {/* Status dropdown */}
          <span style={{ position: 'relative' }}>
            <button
              className="btn btn-secondary btn-small"
              style={{ fontWeight: 500 }}
              onClick={() => setShowStatusDropdown(v => !v)}
              disabled={statusChanging}
            >
              {statusChanging ? 'Updating...' : STATUS_LABELS[client.status]}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '0.5rem' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showStatusDropdown && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 1000, minWidth: '180px', overflow: 'hidden' }}>
                {STATUS_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    className="btn btn-ghost"
                    style={{ width: '100%', textAlign: 'left', borderRadius: 0, padding: '0.75rem 1rem', background: client.status === option.value ? 'rgba(139,92,246,0.2)' : 'transparent', fontWeight: client.status === option.value ? 600 : 400 }}
                    onClick={() => handleStatusChange(option.value)}
                  >
                    {option.label}
                    {client.status === option.value && ' (current)'}
                  </button>
                ))}
              </div>
            )}
          </span>
          {client.status_changed_at && (
            <span className="text-muted" style={{ fontSize: '0.875rem' }}>
              (since {new Date(client.status_changed_at).toLocaleDateString()})
            </span>
          )}
          <span className="text-muted">|</span>
          <span>{client.region.toUpperCase()}</span>
          <span className="text-muted">|</span>
          <span>{client.aum_bucket.replace('_', ' ')}</span>
          <button className="btn btn-secondary btn-small" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }} onClick={handleShowStatusHistory}>
            {showStatusHistory ? 'Hide History' : 'Status History'}
          </button>
        </p>

        {/* Mood strip (last 8 meetings) */}
        {moodData.length > 0 && (
          <div className="mood-strip" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'var(--bg-secondary)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span className="text-small text-muted">Sentiment:</span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {moodData.map(meeting => {
                const dot = getSentimentDot(meeting);
                return (
                  <div
                    key={meeting.transcript_id}
                    className={`sentiment-dot ${dot.opacity < 1 ? 'low-confidence' : ''}`}
                    style={{ backgroundColor: dot.color, cursor: 'pointer', position: 'relative' }}
                    title={`${meeting.meeting_date} - ${meeting.meeting_type}`}
                    onClick={() => navigate(`/transcripts/${meeting.transcript_id}`)}
                    onMouseEnter={() => setHoveredMeeting(meeting)}
                    onMouseLeave={() => setHoveredMeeting(null)}
                  />
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', marginLeft: 'auto' }}>
              {(Object.entries(CAP8_LABELS) as [SentimentCap8, string][]).map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <div className="sentiment-dot" style={{ backgroundColor: CAP8_COLORS[key], width: '8px', height: '8px' }} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mood tooltip */}
        {hoveredMeeting && (
          <div className="sentiment-tooltip" style={{ position: 'absolute', background: 'var(--bg-tertiary)', padding: '0.75rem', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 1000, maxWidth: '300px', marginTop: '0.5rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{hoveredMeeting.meeting_date} - {hoveredMeeting.meeting_type}</div>
            <div style={{ marginBottom: '0.25rem' }}>
              <strong>Sentiment:</strong> {hoveredMeeting.sentiment_cap8 ? CAP8_LABELS[hoveredMeeting.sentiment_cap8] : 'N/A'}
              <span className="text-muted"> ({hoveredMeeting.sentiment_cap8_confidence || 'N/A'} confidence)</span>
            </div>
            {hoveredMeeting.sentiment_cap8_reasoning && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{hoveredMeeting.sentiment_cap8_reasoning}</div>
            )}
          </div>
        )}

        {/* Status history */}
        {showStatusHistory && (
          <div className="glass-card" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>Status History</h3>
            {statusHistory.length === 0 ? (
              <p className="text-muted">No status changes recorded</p>
            ) : (
              <div className="status-list">
                {statusHistory.map(h => (
                  <div key={h.history_id} className="status-item" style={{ padding: '0.5rem 0' }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>
                        {h.old_status ? `${STATUS_LABELS[h.old_status]} → ` : ''}{STATUS_LABELS[h.new_status]}
                      </span>
                      {h.notes && <span className="text-muted" style={{ marginLeft: '0.5rem' }}>({h.notes})</span>}
                    </div>
                    <span className="text-muted text-small">{new Date(h.changed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Tab bar */}
      <div className="tab-bar" style={{ marginBottom: '1.5rem' }}>
        {(['overview', 'commitments', 'sentiment', 'asks'] as TabType[]).map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => handleTabChange(tab)}
          >
            {tab === 'overview'     ? 'Overview'
            : tab === 'commitments' ? 'Commitments'
            : tab === 'sentiment'   ? 'Sentiment'
            :                         'Asks & Demand'}
          </button>
        ))}
      </div>

      {/* ── Overview tab ─────────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="glass-card">
          {/* Brief type toggle */}
          <div className="filter-tabs" style={{ marginBottom: '1.25rem' }}>
            <button
              className={`filter-tab ${briefType === 'internal' ? 'active' : ''}`}
              onClick={() => setBriefType('internal')}
            >
              Internal Brief
            </button>
            <button
              className={`filter-tab ${briefType === 'shareable' ? 'active' : ''}`}
              onClick={() => {
                setBriefType('shareable');
                if (!shareableBrief) setShareableConfirmOpen(true);
              }}
            >
              Client Brief
            </button>
          </div>

          {briefType === 'internal' && renderBriefContent(internalBrief, 'internal')}

          {briefType === 'shareable' && (
            shareableConfirmOpen ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Generate Client-Shareable Brief</h3>
                <p className="text-muted" style={{ marginBottom: '1.5rem' }}>
                  This will create a brief appropriate for sharing with the client.
                  It will exclude internal assessments, pricing discussions, and competitive mentions.
                </p>
                <div className="btn-group">
                  <button className="btn btn-primary" onClick={() => { setShareableConfirmOpen(false); loadBrief('shareable'); }}>
                    Generate Brief
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setShareableConfirmOpen(false); setBriefType('internal'); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              renderBriefContent(shareableBrief, 'shareable')
            )
          )}
        </div>
      )}

      {/* ── Commitments tab ──────────────────────────────────────────────── */}
      {activeTab === 'commitments' && (
        commitmentsLoading ? (
          <div className="loading">Loading commitments...</div>
        ) : commitmentSummary ? (
          <CommitmentsTab
            summary={commitmentSummary}
            onCommitmentStatus={handleCommitmentStatus}
            onStandingItemComplete={handleStandingItemComplete}
          />
        ) : null
      )}

      {/* ── Sentiment tab ────────────────────────────────────────────────── */}
      {activeTab === 'sentiment' && (
        sentimentLoading ? (
          <div className="loading">Loading sentiment...</div>
        ) : moodGrid ? (
          <SentimentTab
            moodGrid={moodGrid}
            expandedMeetings={expandedMeetings}
            onToggle={toggleMeeting}
          />
        ) : null
      )}

      {/* ── Asks & Demand tab ────────────────────────────────────────────── */}
      {activeTab === 'asks' && (
        asksLoading ? (
          <div className="loading">Loading asks...</div>
        ) : asks ? (
          <AsksTab asks={asks} />
        ) : null
      )}
    </div>
  );
}

// ── Commitments sub-component ──────────────────────────────────────────────

interface CommitmentsTabProps {
  summary: ClientSummary;
  onCommitmentStatus: (id: string, status: CommitmentStatus) => void;
  onStandingItemComplete: (id: string) => void;
}

function CommitmentsTab({ summary, onCommitmentStatus, onStandingItemComplete }: CommitmentsTabProps) {
  const { open_commitments, standing_items } = summary;

  const cap8 = open_commitments.filter(c => c.owner === 'cap8');
  const clientOwned = open_commitments.filter(c => c.owner === 'client');
  const other = open_commitments.filter(c => c.owner !== 'cap8' && c.owner !== 'client');

  function AttentionPill({ commitment }: { commitment: typeof open_commitments[0] }) {
    if (isOverdue(commitment.due_date)) return <span className="attention-pill attention-pill-overdue">Overdue</span>;
    if (isUpcoming(commitment.due_date)) return <span className="attention-pill attention-pill-upcoming">Due soon</span>;
    if (commitment.owner === 'client' && isStaleWaiting(commitment.meeting_date)) return <span className="attention-pill attention-pill-stale">Stale</span>;
    return null;
  }

  return (
    <>
      {/* Standing Items */}
      <div className="glass-card mb-3">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          Standing Items ({standing_items.length})
        </h2>
        {standing_items.length === 0 ? (
          <p className="text-muted">No standing items</p>
        ) : (
          <div className="status-list">
            {standing_items.map(item => (
              <div key={item.standing_item_id} className="status-item">
                <div>
                  <div style={{ fontWeight: 500 }}>{item.item_text}</div>
                  <div className="text-small text-muted">
                    {CADENCE_LABELS[item.cadence]}
                    {item.last_completed_at && ` | Last: ${item.last_completed_at.split('T')[0]}`}
                  </div>
                </div>
                <button className="btn btn-secondary btn-small" onClick={() => onStandingItemComplete(item.standing_item_id)}>
                  Complete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cap8 commitments */}
      <div className="glass-card mb-3">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          <span className="tag tag-cap8" style={{ marginRight: '0.5rem' }}>Cap8</span>
          Our Commitments ({cap8.length})
        </h2>
        {cap8.length === 0 ? (
          <p className="text-muted">No open commitments from Cap8</p>
        ) : (
          <div className="status-list">
            {cap8.map(c => (
              <div key={c.commitment_id} className="status-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                    {c.commitment_text}
                    <AttentionPill commitment={c} />
                  </div>
                  <div className="text-small text-muted">
                    {c.meeting_date}
                    {c.due_date && (
                      <span style={{ marginLeft: '1rem', color: isOverdue(c.due_date) ? 'var(--error)' : 'inherit' }}>
                        Due: {c.due_date}
                      </span>
                    )}
                  </div>
                </div>
                <div className="btn-group mt-1">
                  <button className="btn btn-primary btn-small" onClick={() => onCommitmentStatus(c.commitment_id, 'done')}>Mark Done</button>
                  <button className="btn btn-secondary btn-small" onClick={() => onCommitmentStatus(c.commitment_id, 'cancelled')}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Client commitments */}
      <div className="glass-card mb-3">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          <span className="tag tag-client" style={{ marginRight: '0.5rem' }}>Client</span>
          Waiting on Client ({clientOwned.length})
        </h2>
        {clientOwned.length === 0 ? (
          <p className="text-muted">No open commitments from client</p>
        ) : (
          <div className="status-list">
            {clientOwned.map(c => (
              <div key={c.commitment_id} className="status-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                    {c.commitment_text}
                    <AttentionPill commitment={c} />
                  </div>
                  <div className="text-small text-muted">
                    {c.meeting_date}
                    {c.due_date && <span style={{ marginLeft: '1rem' }}>Due: {c.due_date}</span>}
                  </div>
                </div>
                <div className="btn-group mt-1">
                  <button className="btn btn-primary btn-small" onClick={() => onCommitmentStatus(c.commitment_id, 'done')}>Mark Done</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other commitments */}
      {other.length > 0 && (
        <div className="glass-card">
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
            Other Commitments ({other.length})
          </h2>
          <div className="status-list">
            {other.map(c => (
              <div key={c.commitment_id} className="status-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ width: '100%' }}>
                  <span className={`tag tag-${c.owner}`} style={{ marginRight: '0.5rem' }}>{OWNER_LABELS[c.owner]}</span>
                  <span style={{ fontWeight: 500 }}>{c.commitment_text}</span>
                  <AttentionPill commitment={c} />
                  <div className="text-small text-muted mt-1">
                    {c.meeting_date}
                    {c.due_date && <span style={{ marginLeft: '1rem' }}>Due: {c.due_date}</span>}
                  </div>
                </div>
                <div className="btn-group mt-1">
                  <button className="btn btn-primary btn-small" onClick={() => onCommitmentStatus(c.commitment_id, 'done')}>Mark Done</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ── Sentiment sub-component ────────────────────────────────────────────────

const MOOD_PILL_COLORS: Record<SentimentCap8, string> = {
  negative:     'tag-invalid',
  neutral:      'tag-default',
  positive:     'tag-treated',
  very_positive:'tag-valid',
};

interface SentimentTabProps {
  moodGrid: MoodGridResponse;
  expandedMeetings: Set<string>;
  onToggle: (id: string) => void;
}

function SentimentTab({ moodGrid, expandedMeetings, onToggle }: SentimentTabProps) {
  const clientData = moodGrid.clients[0];
  if (!clientData || clientData.meetings.length === 0) {
    return (
      <div className="glass-card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p className="text-muted">No sentiment data recorded yet.</p>
      </div>
    );
  }

  return (
    <div className="glass-card">
      <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
        Sentiment History ({clientData.meetings.length} meetings)
      </h2>
      <div className="status-list">
        {clientData.meetings.map(meeting => {
          const expanded = expandedMeetings.has(meeting.transcript_id);
          const capClass = meeting.sentiment_cap8 ? MOOD_PILL_COLORS[meeting.sentiment_cap8] : 'tag-default';
          return (
            <div key={meeting.transcript_id}>
              <div
                className="status-item"
                style={{ cursor: 'pointer' }}
                onClick={() => onToggle(meeting.transcript_id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                  <span className="text-small text-muted" style={{ minWidth: '6rem' }}>{meeting.meeting_date}</span>
                  {meeting.sentiment_cap8 ? (
                    <span className={`tag ${capClass}`}>
                      {CAP8_LABELS[meeting.sentiment_cap8]}
                      {meeting.sentiment_cap8_confidence && (
                        <span style={{ marginLeft: '0.375rem', opacity: 0.7, fontSize: '0.7rem' }}>
                          ({meeting.sentiment_cap8_confidence})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted text-small">No cap8 sentiment</span>
                  )}
                  {meeting.sentiment_market && (
                    <span className={`mood-badge mood-${meeting.sentiment_market} small`}>
                      {meeting.sentiment_market}
                    </span>
                  )}
                </div>
                <span className="text-muted text-small">{expanded ? '(hide)' : '(expand)'}</span>
              </div>
              {expanded && (
                <div className="mood-row-reasoning">
                  {meeting.sentiment_cap8_reasoning ? (
                    <div style={{ marginBottom: meeting.sentiment_market_reasoning ? '0.5rem' : 0 }}>
                      <strong>Cap8:</strong> {meeting.sentiment_cap8_reasoning}
                    </div>
                  ) : (
                    <span className="text-muted">No cap8 reasoning recorded.</span>
                  )}
                  {meeting.sentiment_market_reasoning && (
                    <div>
                      <strong>Market:</strong> {meeting.sentiment_market_reasoning}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Asks & Demand sub-component ────────────────────────────────────────────

interface AsksTabProps {
  asks: ClientAsksResponse;
}

function getStrategyClass(strategy: string): string {
  const classes: Record<string, string> = {
    low_volatility: 'tag-strategy-low-vol',
    sp500_tilts: 'tag-strategy-tilts',
    sector_rotation: 'tag-strategy-sector',
    dynamic_hedge: 'tag-strategy-hedge',
    risk_overlay: 'tag-strategy-overlay',
    core_equity: 'tag-strategy-core',
    novel: 'tag-strategy-novel',
  };
  return `tag ${classes[strategy] || 'tag-default'}`;
}

function getPositionClass(position: string): string {
  const classes: Record<string, string> = {
    using_it: 'tag-position-using',
    considering_it: 'tag-position-considering',
    asking_about_it: 'tag-position-asking',
    pushed_back_on_it: 'tag-position-pushback',
    mentioned_in_passing: 'tag-position-passing',
    recommending_against_it: 'tag-position-against',
  };
  return `tag ${classes[position] || 'tag-default'}`;
}

function DispositionChip({ insight }: { insight: ClientAsksResponse['insights'][0] }) {
  const { disposition, rejection_reason, linked_roadmap } = insight;

  if (disposition === 'linked') {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
        {linked_roadmap.map(item => (
          <Link
            key={item.roadmap_item_id}
            to={`/roadmap/${item.roadmap_item_id}`}
            className="badge badge-blue"
            style={{ textDecoration: 'none', fontSize: '0.75rem' }}
            onClick={e => e.stopPropagation()}
          >
            {item.title}
          </Link>
        ))}
      </div>
    );
  }

  if (disposition === 'rejected') {
    return (
      <span
        className="disposition-rejected"
        title={rejection_reason || undefined}
        style={{ marginTop: '0.25rem', display: 'inline-block' }}
      >
        Rejected{rejection_reason ? ` — ${rejection_reason.slice(0, 40)}${rejection_reason.length > 40 ? '...' : ''}` : ''}
      </span>
    );
  }

  // pending
  return (
    <Link
      to="/roadmap?tab=triage"
      className="disposition-pending"
      style={{ textDecoration: 'none', marginTop: '0.25rem', display: 'inline-block' }}
      onClick={e => e.stopPropagation()}
    >
      In triage &rarr;
    </Link>
  );
}

function AsksTab({ asks }: AsksTabProps) {
  const [expandedInsights, setExpandedInsights] = useState<Set<string>>(new Set());

  const toggleInsight = (id: string) => {
    setExpandedInsights(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <>
      {/* Insights */}
      <div className="glass-card mb-3">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          Product Asks ({asks.insights.length})
        </h2>
        {asks.insights.length === 0 ? (
          <p className="text-muted">No insights extracted for this client yet.</p>
        ) : (
          <div className="status-list">
            {asks.insights.map(insight => {
              const expanded = expandedInsights.has(insight.insight_id);
              return (
                <div key={insight.insight_id}>
                  <div
                    className="status-item"
                    style={{ cursor: 'pointer', flexDirection: 'column', alignItems: 'flex-start' }}
                    onClick={() => toggleInsight(insight.insight_id)}
                  >
                    <div style={{ width: '100%', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{insight.insight_text}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
                          <span className="text-small text-muted">{insight.meeting_date}</span>
                          <BranchChip branch={insight.branch as Branch | null} editable={false} />
                          <DispositionChip insight={insight} />
                        </div>
                      </div>
                    </div>
                    {expanded && (
                      <div style={{ marginTop: '0.5rem', width: '100%' }}>
                        <div className="quote">{insight.supporting_quote}</div>
                        <div className="text-small text-muted mt-1">
                          Urgency: {insight.urgency} | Module: {insight.module_touched.replace('_', ' ')} | Confidence: {insight.extraction_confidence}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Strategy demand */}
      <div className="glass-card">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>
          Strategy Demand ({asks.strategy_mentions.length})
        </h2>
        {asks.strategy_mentions.length === 0 ? (
          <p className="text-muted">No strategy mentions for this client.</p>
        ) : (
          <div className="status-list">
            {asks.strategy_mentions.map(mention => (
              <div key={mention.mention_id} className="status-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center', width: '100%' }}>
                  <span className={getStrategyClass(mention.strategy)}>
                    {STRATEGY_LABELS[mention.strategy] || mention.strategy}
                  </span>
                  <span className={getPositionClass(mention.client_position)}>
                    {POSITION_LABELS[mention.client_position] || mention.client_position}
                  </span>
                  <span className="text-small text-muted" style={{ marginLeft: 'auto' }}>{mention.meeting_date}</span>
                </div>
                <div style={{ marginTop: '0.25rem', fontSize: '0.875rem' }}>{mention.summary}</div>
                {mention.is_end_client_demand && (
                  <span className="tag tag-end-client" style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>End-Client</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
